import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { getAuthHeaders, removeToken } from '../utils/auth'
import { useUser } from '../contexts/UserContext'
import { Menu, X, Building2, Settings, LogOut } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_BASE

function InitialsAvatar({ name, email, avatarUrl }) {
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Check if string contains only English letters
  const isValidEnglishLetters = (str) => {
    // Allow English letters, spaces, and common name punctuation
    return /^[A-Za-z\s'.,-]+$/.test(str) && /[A-Za-z]/.test(str);
  };

  // Check if string contains RTL characters (Hebrew or Arabic)
  const isRTL = (str) => {
    return /[\u0590-\u05FF\u0600-\u06FF]/.test(str);
  };

  // Get initials from full name if available, otherwise from email
  const getInitials = () => {
    if (name && name.trim()) {
      // Split by spaces and take first two words (using original name for RTL check)
      const nameParts = name.split(' ').filter(part => part.length > 0).slice(0, 2);
      
      if (nameParts.length > 0) {
        // Check if name contains RTL characters first
        if (isRTL(name)) {
          // For RTL languages like Hebrew or Arabic, take first letter of each word
          return nameParts
            .map(word => word.charAt(0))
            .join('');
        }
        
        // For non-RTL names, clean and check for English letters
        const cleanName = name.replace(/[^A-Za-z\s]/g, '').trim();
        const cleanParts = cleanName.split(' ').filter(part => part.length > 0).slice(0, 2);
        
        // For names with English letters (even if they contain punctuation)
        if (cleanParts.some(part => /[A-Za-z]/.test(part))) {
          return cleanParts
            .map(word => word[0])
            .join('')
            .toUpperCase();
        }

        // For other languages or invalid names, use email initials
        if (email) {
          const [localPart] = email.split('@');
          const emailParts = localPart.split('.');
          if (emailParts.length >= 2) {
            return emailParts.slice(0, 2)
              .map(part => part[0])
              .join('')
              .toUpperCase();
          }
          // If email doesn't have dots, use first two characters
          return localPart.slice(0, 2).toUpperCase();
        }
      }
    }

    // Fallback to email initial only if no name is provided
    return email ? email[0].toUpperCase() : '';
  };

  // Reset states when avatarUrl changes
  useEffect(() => {
    if (avatarUrl) {
      setIsLoading(true);
      setImageError(false);
    }
  }, [avatarUrl]);

  // If we have an avatar URL, try to load it
  if (avatarUrl && !imageError) {
    return (
      <div className={`w-11 h-11 rounded-full overflow-hidden ${!isLoading ? 'ring-1 ring-white/20' : ''}`}>
        <img
          src={avatarUrl}
          alt={name || email}
          className="w-full h-full object-cover"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setImageError(true);
          }}
        />
      </div>
    );
  }

  // If no avatar URL or image failed to load, show initials
  const initials = getInitials();
  if (initials) {
    return (
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center text-lg font-medium text-white ring-1 ring-white/20"
        style={{ backgroundColor: '#B356C6' }}
      >
        <span dir={isRTL(initials) ? 'rtl' : 'ltr'}>
          {initials}
        </span>
      </div>
    );
  }

  // If no avatar and no initials, show nothing
  return null;
}

export default function HomeLayout({ children }) {
  const [showDropdown, setShowDropdown] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const dropdownRef = useRef(null)
  const sidebarRef = useRef(null)
  const navigate = useNavigate()
  const { userData } = useUser()
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  
  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determine if we're in mobile or desktop view
  const isMobile = windowWidth < 1024; // lg breakpoint in Tailwind

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
      // Only close sidebar on mobile view when clicking outside
      if (isMobile && sidebarRef.current && !sidebarRef.current.contains(event.target) && showSidebar && !isClosing) {
        handleCloseSidebar()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showSidebar, isClosing, isMobile])

  const handleCloseSidebar = () => {
    setIsClosing(true)
    setTimeout(() => {
      setShowSidebar(false)
      setIsClosing(false)
    }, 300) // Match this with animation duration
  }

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      const token = sessionStorage.getItem('jwt');
      if (!token) {
        removeToken();
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Always clean up, regardless of response
      removeToken();
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login', { replace: true });
      
    } catch (err) {
      console.error('Logout error:', err);
      // Even if the request fails, clean up local data
      removeToken();
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login', { replace: true });
    } finally {
      setLoggingOut(false)
    }
  }

  const handleProfile = () => {
    navigate('/user/settings')
    setShowDropdown(false)
  }

  const handleCompanyDetails = () => {
    navigate('/company/details')
    setShowDropdown(false)
  }

  return (
    <div className="min-h-screen max-h-screen bg-gradient-to-b from-[var(--bg-start)] to-[var(--bg-end)] overflow-hidden flex flex-col">
      {/* Background */}
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-[var(--bg-start)] to-[var(--bg-end)]">
        <div
          className="absolute inset-0 bg-cover bg-center brightness-125"
          style={{ backgroundImage: `url('/assets/bg_blur.webp')` }}
        />
      </div>

      {/* Header - Always visible */}
      <header className="sticky top-0 z-50 w-full bg-[var(--accent-secondary)] bg-opacity-95 border-b border-[var(--border-color)]">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              {/* Hamburger Menu Button */}
              <button
                onClick={() => {
                  if (isMobile) {
                    // Mobile behavior - toggle sidebar visibility
                    if (!isClosing) {
                      showSidebar ? handleCloseSidebar() : setShowSidebar(true);
                    }
                  } else {
                    // Desktop behavior - toggle sidebar collapsed state
                    setDesktopSidebarCollapsed(!desktopSidebarCollapsed);
                  }
                }}
                className="p-2 rounded-md text-white hover:bg-[var(--hover-light)] transition-colors duration-200"
              >
                {(isMobile && showSidebar && !isClosing) || (!isMobile && !desktopSidebarCollapsed) ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>

              <div 
                className="flex items-center cursor-pointer"
                onClick={() => navigate('/home')}
              >
                <img 
                  src="/images/logo.png" 
                  alt="Toritech Logo" 
                  className="w-8 h-8 mr-2" 
                />
                <h1 className="text-xl font-bold text-white">Toritech</h1>
              </div>
            </div>
            
            {/* User Profile Area */}
            <div className="flex items-center gap-3">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center focus:outline-none"
                  type="button"
                >
                  <InitialsAvatar 
                    name={userData?.fullName} 
                    email={userData?.email} 
                    avatarUrl={userData?.avatarUrl}
                  />
                </button>

                <div 
                  className={`absolute right-0 mt-2 w-56 rounded-xl overflow-hidden shadow-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-purple-100 dark:border-purple-900 transition-opacity duration-150 ${
                    showDropdown ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}
                  style={{ 
                    zIndex: 100, 
                    top: 'calc(100% + 4px)',
                    transform: 'none'
                  }}
                >
                  <div className="py-2">
                    <div className="px-4 py-3.5 text-sm border-b border-purple-100 dark:border-purple-900 opacity-90">
                      {userData?.fullName && (
                        <div className="font-medium text-gray-700 dark:text-gray-300 text-center mb-2">
                          {userData.fullName}
                        </div>
                      )}
                      {userData?.company && typeof userData.company === 'object' && 
                       userData.company.name && userData.company.name !== userData.fullName && (
                        <div className="text-purple-600 dark:text-purple-400 text-center mb-2">
                          <span className="text-sm font-medium">
                            {userData.company.name}
                          </span>
                        </div>
                      )}
                      <div className="text-gray-500 dark:text-gray-400 italic opacity-80 text-center text-xs">
                        {userData?.email}
                      </div>
                    </div>
                    <div
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors duration-150 cursor-pointer"
                      onClick={handleProfile}
                    >
                      <div className="mr-2 text-purple-500">
                        <Settings className="h-4 w-4" />
                      </div>
                      Profile
                    </div>
                    <div
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors duration-150 cursor-pointer"
                      onClick={handleLogout}
                    >
                      <div className="mr-2 text-red-500">
                        <LogOut className="h-4 w-4" />
                      </div>
                      Logout
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area with Sidebar */}
      <div className="relative flex-1 overflow-hidden">
        {/* Mobile Backdrop - only shown on mobile */}
        {isMobile && (showSidebar || isClosing) && (
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300"
            style={{
              opacity: isClosing ? 0 : 1
            }}
            onClick={handleCloseSidebar}
          />
        )}
        
        {/* Sidebar - Always rendered on desktop, conditionally on mobile */}
        <div 
          ref={sidebarRef}
          className={`absolute top-0 h-full w-80 bg-[var(--card-bg)] border-r border-[var(--border-color)] z-50 transform transition-transform duration-300 ease-out overflow-y-auto ${
            // Different classes based on device and state
            isMobile 
              ? (showSidebar || isClosing) 
                ? "left-0" 
                : "-left-80" 
              : desktopSidebarCollapsed 
                ? "-left-80 lg:left-0 lg:opacity-0 lg:invisible" 
                : "left-0 lg:opacity-100 lg:visible"
          }`}
          style={{ 
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
            animation: isMobile && isClosing ? 'slideOut 0.3s ease-out forwards' : 
                       isMobile && showSidebar ? 'slideIn 0.3s ease-out' : 'none'
          }}
        >
          <style>
            {`
              @keyframes slideIn {
                from { transform: translateX(-100%); }
                to { transform: translateX(0); }
              }
              @keyframes slideOut {
                from { transform: translateX(0); }
                to { transform: translateX(-100%); }
              }
            `}
          </style>

          {/* Sidebar Content */}
          <div className="p-4">
            {/* Company Settings Section (only for Admin/Owner) */}
            {userData && ['Owner', 'Admin'].includes(userData.permissionsId) && (
              <div className="mb-6">
                <div className="flex items-center gap-2 px-4 mb-3">
                  <Building2 className="h-5 w-5 text-purple-400" />
                  <span className="text-sm font-medium text-[var(--text-secondary)]">Company Settings</span>
                  <span className="text-[13px] text-[var(--text-secondary)] ml-auto">({userData.permissionsId})</span>
                </div>
                <div className="pl-4">
                  <button
                    onClick={() => {
                      navigate('/company/details');
                      isMobile && handleCloseSidebar();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors duration-200"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Details</span>
                      <span className="text-xs text-[var(--text-secondary)]">Profile & contact details</span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/company/users');
                      isMobile && handleCloseSidebar();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors duration-200"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Users</span>
                      <span className="text-xs text-[var(--text-secondary)]">Manage user access</span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/company/teams');
                      isMobile && handleCloseSidebar();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors duration-200"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Teams</span>
                      <span className="text-xs text-[var(--text-secondary)]">Manage team structures</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
            
            {/* Add more menu items here */}
          </div>
        </div>

        {/* Main Content - Adjust margin for desktop sidebar */}
        <main className={`relative z-10 h-full overflow-y-auto transition-all duration-300 ${
          !isMobile && !desktopSidebarCollapsed ? 'lg:ml-80' : ''
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
} 